import React, { PropsWithChildren, useState } from "react";
import { WithThumbnailFile } from "../../../../common/types/Data/File";
import { PopulatedQuiz } from "../../../../common/types/Data/PopulatedQuiz";
import { PopulatedQuizQuestion } from "../../../../common/types/Data/PopulatedQuizQuestion";
import { QuizType } from "../../../../common/types/Data/QuizType";
import { QuizAnswer } from "./QuizAnswer";
import { QuizAnswer as QuizAnswerData } from "../../../../common/types/Data/QuizAnswer";
import { QuizQuestionImage } from "./QuizQuestionImage";

type Props = {
    quiz: PopulatedQuiz;
    currentQuizQuestion: PopulatedQuizQuestion;
    quizQuestion: PopulatedQuizQuestion;
    onAnswerSelected: (quizQuestion: PopulatedQuizQuestion, quizAnswer: QuizAnswerData) => void;
};

const waitFor = (milliseconds: number): Promise<void> => {
    return new Promise<void>((resolve) => {
        setTimeout(resolve, milliseconds);
    });
};

const getCorrectAnswer = (quiz: PopulatedQuiz, quizQuestion: PopulatedQuizQuestion) => {
    // No right answer
    if (quiz.quizType === QuizType.Personality) {
        return;
    }

    // Sort answers by score, and return the best
    return [...quizQuestion.quizAnswers]
        .sort((a, b) => {
            return Number.parseInt(`${a.score || 0}`, 10) - Number.parseInt(`${b.score || 0}`, 10);
        })
        .pop();
};

export const QuizQuestion = (props: PropsWithChildren<Props>) => {
    const { quiz, quizQuestion, currentQuizQuestion, onAnswerSelected } = props;

    const quizQuestionIndex = quiz.quizQuestions.indexOf(quizQuestion);
    const currentQuizQuestionIndex = quiz.quizQuestions.indexOf(currentQuizQuestion);
    const correctAnswer = getCorrectAnswer(quiz, quizQuestion);

    const [revealed, setRevealed] = useState(quiz.quizType !== QuizType.ChallengeReveal);
    const [highlightAnswer, setHighlightAnswer] = useState(false);

    // Choose which class to render the parent with
    const parentClass = ["quiz-question", revealed ? "revealed" : ""];

    if (quizQuestionIndex < currentQuizQuestionIndex) {
        parentClass.push("left");
    } else if (quizQuestionIndex > currentQuizQuestionIndex) {
        parentClass.push("right");
    }

    const onAnswerSelectedFactory = (quizAnswer: QuizAnswerData & WithThumbnailFile) => {
        return async () => {
            if (quiz.quizType === QuizType.ChallengeReveal) {
                setRevealed(true);
                setHighlightAnswer(true);
                await waitFor(2000);
                onAnswerSelected(quizQuestion, quizAnswer);
            } else {
                onAnswerSelected(quizQuestion, quizAnswer);
            }
        };
    };

    const showQuestionImage = quiz.quizType === QuizType.ChallengeReveal || quiz.quizType === QuizType.Slider;

    return (
        <div className={parentClass.join(" ")} key={quizQuestion.id}>
            {showQuestionImage ? <QuizQuestionImage quizQuestion={quizQuestion} revealed={revealed} /> : undefined}
            <div className={"quiz-answers"}>
                {quizQuestion.quizAnswers.map((quizAnswer) => {
                    return <QuizAnswer onAnswerSelected={onAnswerSelectedFactory(quizAnswer)} key={quizAnswer.id} quizAnswer={quizAnswer} highlight={highlightAnswer && quizAnswer === correctAnswer} />;
                })}
            </div>
        </div>
    );
};
